import { useState, useMemo, useRef, useEffect } from "react";
import { Download, Filter, X, Calendar, ChevronDown, Tag } from "lucide-react";
import { Input } from "../../../components/ui/input";
import { tagChipStyle } from "./dealTagsApi";

// --- Date-range presets (calculated lazily — based on today, UTC) ---
const todayISO = () => new Date().toISOString().split("T")[0];
const offsetISO = (days) => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().split("T")[0];
};
const startOfMonthISO = (monthOffset = 0) => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + monthOffset);
  return d.toISOString().split("T")[0];
};
const endOfMonthISO = (monthOffset = 0) => {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + monthOffset + 1);
  d.setUTCDate(0);
  return d.toISOString().split("T")[0];
};
const startOfYearISO = (yearOffset = 0) => {
  const d = new Date();
  return `${d.getUTCFullYear() + yearOffset}-01-01`;
};
const endOfYearISO = (yearOffset = 0) => {
  const d = new Date();
  return `${d.getUTCFullYear() + yearOffset}-12-31`;
};

export const DATE_PRESETS = [
  { id: "yesterday", label: "Yesterday", range: () => ({ date_from: offsetISO(1), date_to: offsetISO(1) }) },
  { id: "last7", label: "Last 7 Days", range: () => ({ date_from: offsetISO(7), date_to: todayISO() }) },
  { id: "last30", label: "Last 30 Days", range: () => ({ date_from: offsetISO(30), date_to: todayISO() }) },
  { id: "last90", label: "Last 90 Days", range: () => ({ date_from: offsetISO(90), date_to: todayISO() }) },
  { id: "this_month", label: "This Month", range: () => ({ date_from: startOfMonthISO(0), date_to: todayISO() }) },
  { id: "last_month", label: "Last Month", range: () => ({ date_from: startOfMonthISO(-1), date_to: endOfMonthISO(-1) }) },
  { id: "this_year", label: "This Year", range: () => ({ date_from: startOfYearISO(0), date_to: todayISO() }) },
  { id: "last_year", label: "Last Year", range: () => ({ date_from: startOfYearISO(-1), date_to: endOfYearISO(-1) }) },
  { id: "all_time", label: "All Time", range: () => ({ date_from: "", date_to: "" }) },
  { id: "custom", label: "Custom Range…", range: null },
];

// Detect which preset matches the current date_from/date_to, or "custom"
const detectPreset = (date_from, date_to) => {
  for (const p of DATE_PRESETS) {
    if (!p.range) continue;
    const r = p.range();
    if (r.date_from === date_from && r.date_to === date_to) return p.id;
  }
  return "custom";
};

const MarketplaceFilters = ({ filters, onApply, onExport, breakdownBySSP, lightMode = false, tags = [], isSuperAdmin = false }) => {
  const [expanded, setExpanded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const tagMenuRef = useRef(null);
  const [local, setLocal] = useState({ ...filters });

  const sspSources = useMemo(
    () => (breakdownBySSP || []).map((b) => b.label).filter((l) => l && l !== "(empty)"),
    [breakdownBySSP]
  );

  const selectedTagIds = filters.tag_ids || [];
  const tagsById = useMemo(() => {
    const m = new Map();
    tags.forEach((t) => m.set(t.tag_id, t));
    return m;
  }, [tags]);

  useEffect(() => {
    if (!tagMenuOpen) return;
    const onClick = (e) => {
      if (tagMenuRef.current && !tagMenuRef.current.contains(e.target)) setTagMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [tagMenuOpen]);

  // Live preset detection based on parent's filters (source of truth)
  const activePreset = detectPreset(filters.date_from, filters.date_to);

  // ---- Action handlers ----
  // Single-field updates are applied IMMEDIATELY (top-bar dropdowns)
  const applyOne = (key, value) => onApply({ ...filters, [key]: value });

  const handlePresetSelect = (presetId) => {
    setDropdownOpen(false);
    const preset = DATE_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    if (presetId === "custom") {
      // Open the full filter panel for manual date input
      setExpanded(true);
      return;
    }
    const range = preset.range();
    onApply({ ...filters, ...range });
  };

  // Expanded-panel local state (apply on click)
  const handleLocalChange = (key, val) => setLocal((p) => ({ ...p, [key]: val }));
  const applyExpanded = () => {
    onApply(local);
    setExpanded(false);
  };

  const clearAll = () => {
    const cleared = { ssp_source: "", dsp: "", deal_name: "", tag_ids: [], date_from: "", date_to: "" };
    setLocal(cleared);
    onApply(cleared);
  };

  const toggleTag = (tagId) => {
    const cur = selectedTagIds;
    const next = cur.includes(tagId) ? cur.filter((t) => t !== tagId) : [...cur, tagId];
    onApply({ ...filters, tag_ids: next });
  };

  const hasFilters =
    !!filters.dsp || !!filters.ssp_source || !!filters.deal_name ||
    (selectedTagIds && selectedTagIds.length > 0) ||
    // Don't count default date range as a filter
    (activePreset !== "last90" && (filters.date_from || filters.date_to));

  const activePresetLabel =
    DATE_PRESETS.find((p) => p.id === activePreset)?.label ||
    (filters.date_from || filters.date_to ? "Custom Range" : "All Time");

  const inputCls = lightMode
    ? "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#ff3737]"
    : "bg-[#1e2124] border-[#2a2d31] text-[#ebebeb] placeholder:text-[#ebebeb]/30 focus:border-[#ff3737]";

  const pillCls = (active) =>
    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-mono border transition-colors ${
      active
        ? "border-[#ff3737]/30 text-[#ff3737] bg-[#ff3737]/5"
        : lightMode
        ? "border-gray-300 text-gray-700 hover:bg-gray-50"
        : "border-white/10 text-[#ebebeb]/60 hover:bg-white/5"
    }`;

  return (
    <div className="space-y-3">
      {/* ===== TOP BAR ===== */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Filters toggle (for DSP + custom range) */}
        <button
          onClick={() => setExpanded(!expanded)}
          className={pillCls(expanded || !!filters.dsp)}
          data-testid="marketplace-filter-toggle"
        >
          <Filter className="w-4 h-4" /> Filters
          {filters.dsp && (
            <span className="bg-[#ff3737] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">1</span>
          )}
        </button>

        {/* Date range preset dropdown */}
        <div className="relative">
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className={pillCls(activePreset !== "last90" && activePreset !== "all_time")}
            data-testid="marketplace-daterange-toggle"
          >
            <Calendar className="w-4 h-4" /> {activePresetLabel}
            <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          </button>
          {dropdownOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
              <div
                className={`absolute left-0 top-full mt-1 z-20 w-48 rounded-lg border shadow-xl py-1 ${
                  lightMode ? "bg-white border-gray-200" : "bg-[#1e2124] border-[#2a2d31]"
                }`}
                data-testid="marketplace-daterange-menu"
              >
                {DATE_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    onClick={() => handlePresetSelect(p.id)}
                    className={`w-full text-left px-3 py-2 text-sm font-mono transition-colors ${
                      activePreset === p.id
                        ? "bg-[#ff3737]/10 text-[#ff3737]"
                        : lightMode
                        ? "text-gray-700 hover:bg-gray-50"
                        : "text-[#ebebeb]/70 hover:bg-white/5"
                    }`}
                    data-testid={`marketplace-daterange-${p.id}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* SSP source dropdown */}
        <select
          value={filters.ssp_source || ""}
          onChange={(e) => applyOne("ssp_source", e.target.value)}
          className={`px-3 py-2 rounded-lg text-sm font-mono border focus:outline-none transition-colors ${inputCls} ${
            filters.ssp_source ? "border-[#ff3737]/30 text-[#ff3737] bg-[#ff3737]/5" : ""
          }`}
          data-testid="marketplace-ssp-quick-filter"
        >
          <option value="">All SSPs</option>
          {sspSources.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {/* Tag multi-select (Super Admin only) */}
        {isSuperAdmin && (
          <div className="relative" ref={tagMenuRef}>
            <button
              type="button"
              onClick={() => setTagMenuOpen((o) => !o)}
              className={pillCls(selectedTagIds.length > 0)}
              data-testid="marketplace-tag-filter-toggle"
            >
              <Tag className="w-4 h-4" />
              {selectedTagIds.length === 0
                ? "All Tags"
                : `${selectedTagIds.length} tag${selectedTagIds.length === 1 ? "" : "s"}`}
              <ChevronDown className="w-3.5 h-3.5 opacity-60" />
            </button>
            {tagMenuOpen && (
              <div
                className={`absolute left-0 top-full mt-1 z-30 w-64 rounded-lg border shadow-xl py-1 ${
                  lightMode ? "bg-white border-gray-200" : "bg-[#1e2124] border-[#2a2d31]"
                }`}
                data-testid="marketplace-tag-filter-menu"
              >
                {tags.length === 0 ? (
                  <p className="px-3 py-2 text-xs font-mono text-[#ebebeb]/40">
                    No tags yet — create one in Manage Uploads.
                  </p>
                ) : (
                  <>
                    <div className="max-h-64 overflow-y-auto py-1">
                      {tags.map((t) => {
                        const on = selectedTagIds.includes(t.tag_id);
                        return (
                          <button
                            key={t.tag_id}
                            type="button"
                            onClick={() => toggleTag(t.tag_id)}
                            className={`w-full flex items-center justify-between px-3 py-1.5 text-xs font-mono ${
                              lightMode ? "text-gray-700 hover:bg-gray-50" : "text-[#ebebeb]/80 hover:bg-white/5"
                            }`}
                            data-testid={`marketplace-tag-filter-${t.tag_id}`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tagChipStyle(t.tag_id).color }} />
                              {t.name}
                              <span className="opacity-40">·{t.deal_count || 0}</span>
                            </span>
                            {on && <span className="text-[#ff3737]">✓</span>}
                          </button>
                        );
                      })}
                    </div>
                    {selectedTagIds.length > 0 && (
                      <button
                        onClick={() => onApply({ ...filters, tag_ids: [] })}
                        className="w-full px-3 py-1.5 text-[11px] font-mono text-[#ebebeb]/50 hover:text-[#ff3737] border-t border-[#2a2d31]"
                        data-testid="marketplace-tag-filter-clear"
                      >
                        Clear selection
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Deal name quick search */}
        <Input
          value={filters.deal_name || ""}
          onChange={(e) => applyOne("deal_name", e.target.value)}
          placeholder="Search deal name…"
          className={`text-sm w-48 ${inputCls} ${filters.deal_name ? "border-[#ff3737]/30" : ""}`}
          data-testid="marketplace-deal-quick-filter"
        />

        {/* Clear-all */}
        {hasFilters && (
          <button
            onClick={clearAll}
            className={`text-xs font-mono flex items-center gap-1 ${
              lightMode ? "text-gray-500 hover:text-gray-700" : "text-[#ebebeb]/40 hover:text-[#ebebeb]/70"
            }`}
            data-testid="clear-filters-btn"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}

        {/* Export */}
        <button
          onClick={onExport}
          className={`ml-auto flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-mono border transition-colors ${
            lightMode
              ? "border-gray-300 text-gray-700 hover:bg-gray-50"
              : "border-white/10 text-[#ebebeb]/60 hover:bg-white/5"
          }`}
          data-testid="marketplace-export-btn"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* ===== EXPANDED FILTERS (DSP + custom date) ===== */}
      {expanded && (
        <div
          className={`rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-3 border ${
            lightMode ? "bg-gray-50 border-gray-200" : "bg-[#1e2124] border-[#2a2d31]"
          }`}
          data-testid="marketplace-filters-panel"
        >
          <div>
            <label className={`text-xs font-mono block mb-1 ${lightMode ? "text-gray-500" : "text-[#ebebeb]/50"}`}>
              DSP / Buyer
            </label>
            <Input
              value={local.dsp}
              onChange={(e) => handleLocalChange("dsp", e.target.value)}
              placeholder="Search DSP…"
              className={`text-sm ${inputCls}`}
              data-testid="filter-dsp"
            />
          </div>
          <div>
            <label className={`text-xs font-mono block mb-1 ${lightMode ? "text-gray-500" : "text-[#ebebeb]/50"}`}>
              Date From
            </label>
            <Input
              type="date"
              value={local.date_from}
              onChange={(e) => handleLocalChange("date_from", e.target.value)}
              className={`text-sm ${inputCls}`}
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <label className={`text-xs font-mono block mb-1 ${lightMode ? "text-gray-500" : "text-[#ebebeb]/50"}`}>
              Date To
            </label>
            <Input
              type="date"
              value={local.date_to}
              onChange={(e) => handleLocalChange("date_to", e.target.value)}
              className={`text-sm ${inputCls}`}
              data-testid="filter-date-to"
            />
          </div>
          <div className="md:col-span-3 flex justify-end gap-2">
            <button
              onClick={() => setExpanded(false)}
              className={`px-4 py-2 rounded-lg text-sm font-mono border transition-colors ${
                lightMode ? "border-gray-300 text-gray-700 hover:bg-gray-100" : "border-white/10 text-[#ebebeb]/60 hover:bg-white/5"
              }`}
              data-testid="filters-cancel-btn"
            >
              Cancel
            </button>
            <button onClick={applyExpanded} className="ingenium-button text-sm px-6" data-testid="apply-filters-btn">
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketplaceFilters;
